import { AbstractControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SsApiService } from '../../services/api/ss-api.service';
import { UserService } from '../../services/user/user.service';
import { Router } from '@angular/router';
import { ValidationPatterns } from '../validation-patterns';
import { catchError, filter, finalize, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { combineLatest, EMPTY, forkJoin, iif, Observable, of, ReplaySubject } from 'rxjs';
import { EnvironmentService } from '../../services/environment.service';
import { PlatformService } from '../../services/platform.service';
import { ToastMessageService } from '../../modules/toast-message/toast-message.service';
import { CacheService } from '../../services/cache/cache.service';
import { ContentUpdaterService } from '../../services/content-updater/content-updater.service';
import { FormsErrorHandlerService } from '../../services/forms-error-handler.service';
import { CustomValidators } from '../custom-validators';
import { EventEmitter, inject, signal } from '@angular/core';
import { GoogleTagManagerService } from '../../services/google-tag-manager.service';
import { UserInfoService } from '../../services/user/user-info.service';
import { GroupsService } from '../../services/groups.service';
import { CookieService } from 'ngx-unificator/services';
import { LocalstorageService } from '../../services/localstorage.service';
import { ArabicService } from '../../services/arabic.service';
import { LoyaltyService } from '../../services/loyalty/loyalty.service';
import { UbidexService } from '../../services/ubidex.service';
import { decodeFromBase64, decodeHex, encodeToBase64, encodeToHex } from '../utils';
import { UserSubscriptionsService } from '../../services/user/user-subscriptions.service';
import { LocalHistoryService } from '../../services/local-history.service';
import { STAG_KEY, STAG_URL_KEY, StagDetectorService } from '../../services/stag-detector.service';
import { CRYPTO_GROUP } from '../../services/user/data/user-group.data';
import { StaticContentService } from '../../services/static-content.service';
import { ZendeskChatService } from '../../services/zendesk/zendesk-chat.service';
import { CountryService } from '../../services/country.service';
import { CurrencyService } from '../../services/currency.service';
import { CFCacheService } from '../../services/cache-control.service';
import { UserProfileService } from '../../services/user/user-profile.service';
import { UserPhoneService } from '../../services/user/user-phone.service';

const fieldsToSaveLocalStorage = ['email', 'password', 'password_confirmation', 'nickname'];

export const registrationStorageName = '__registrationData';
export const additionalInfoStorageName = '__additionalInfo';

const isFullRegStorageName = '__isFulReg';

export class RegisterFormController {

  /**
   * Access to global Services
   */
  private _fb: UntypedFormBuilder = inject(UntypedFormBuilder);
  private _ssApi: SsApiService = inject(SsApiService);
  private _user: UserService = inject(UserService);
  private _userInfo: UserInfoService = inject(UserInfoService);
  private _router: Router = inject(Router);
  private _country = inject(CountryService);
  private _env: EnvironmentService = inject(EnvironmentService);
  private _platform: PlatformService = inject(PlatformService);
  private _toastMessage: ToastMessageService = inject(ToastMessageService);
  private _cache: CacheService = inject(CacheService);
  private _cacheCF = inject(CFCacheService);
  private _cookies: CookieService = inject(CookieService);
  private _contentUpdater: ContentUpdaterService = inject(ContentUpdaterService);
  private _formErrors: FormsErrorHandlerService = inject(FormsErrorHandlerService);
  private _gtm: GoogleTagManagerService = inject(GoogleTagManagerService);
  private _currency = inject(CurrencyService);
  private _groups: GroupsService = inject(GroupsService);
  private _localStorage: LocalstorageService = inject(LocalstorageService);
  private _rtl: ArabicService = inject(ArabicService);
  private _loyalty: LoyaltyService = inject(LoyaltyService);
  private _ubidex: UbidexService = inject(UbidexService);
  private _stag: StagDetectorService = inject(StagDetectorService);
  private _subscriptions: UserSubscriptionsService = inject(UserSubscriptionsService);
  private _local: LocalHistoryService = inject(LocalHistoryService);
  private _static: StaticContentService = inject(StaticContentService);
  private _zendesk: ZendeskChatService = inject(ZendeskChatService);
  private _userProfile: UserProfileService = inject(UserProfileService);
  private _userPhone: UserPhoneService = inject(UserPhoneService);

  /**
   * Is loading state
   */
  public loading = signal(false);

  public isSecondStep = signal(false);

  public enhancedVersion = signal(false);
  /**
   * Arrow for dropdown state
   */
  private _showArrow: boolean;

  /**
   * Emits updating result
   */
  public registered$: EventEmitter<boolean> = new EventEmitter<boolean>();
  public error$: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Login for FormGroup
   */
  private _form: UntypedFormGroup = this._fb.group({
    email: ['', [Validators.required, Validators.pattern(ValidationPatterns.email)]],
    password: ['', [Validators.required,  CustomValidators.changeKey('password_pattern', Validators.pattern(ValidationPatterns.password))]],
    password_confirmation: ['', [Validators.required]],
    nickname: [this._generateNickname(), [Validators.required, CustomValidators.checkSpaces]],
    country: [null, [CustomValidators.changeKey('required_country', Validators.required)]],
    currency: [null, [Validators.required]],
    receive_promos: [true],
    receive_sms_promos: [false],
    terms_acceptance: [false, [Validators.required]],
    age_acceptance: [false, [Validators.required]],
    bonus_code: [null, Validators.pattern(ValidationPatterns.lettersAndNumbers)],
  }, {
    validators: [CustomValidators.mustMatch('password', 'password_confirmation')],
  });

  private _enhancedRegisterForm: FormGroup = this._fb.group({
    nickname: [this._generateNickname(), [Validators.required, CustomValidators.checkSpaces]],
    email: [null, [Validators.required, Validators.pattern(ValidationPatterns.email)]],
    password: ['', [Validators.required,  CustomValidators.changeKey('password_pattern', Validators.pattern(ValidationPatterns.password))]],
    password_confirmation: ['', [Validators.required]],
    currency: [null, [Validators.required]],
    terms_acceptance: [false, [CustomValidators.changeKey('required_true', Validators.requiredTrue)]],
    age_acceptance: [false, [CustomValidators.changeKey('required_true', Validators.requiredTrue)]],
    receive_promos: [true],
    mobile_phone: [null, [Validators.required, Validators.pattern(ValidationPatterns.phoneNumber), Validators.minLength(5)]],
    bonus_code: [null, Validators.pattern(ValidationPatterns.lettersAndNumbers)],
  },
    {validators: [CustomValidators.mustMatch('password', 'password_confirmation')],}
    );

  /**
   * Form errors
   */
  public countryAndNicknameErrors: string[] = [];

  public partners$: Observable<any> = this._getPartners$();

  public enhancedReg$: Observable<any> = this._static.item({ slug: 'full-register-stags' }).pipe(
    filter(data => data?.length),
    tap(() => this.enhancedVersion.set(true)),
  );


  /**
   * True if ptag present
   */
  public isPtag = signal(false);

  public isAllowBonusCodeForPtag$ = this._static.item({ slug: 'available-bonus-code' }).pipe(
    filter((data) => data && data?.length),
    shareReplay(1),
  ) as ReplaySubject<boolean>;

  constructor() {
    this._setAutoDetectedValues();
    this._pasteDataFromStorageToForm();
    this.isPtag.set(this._cookies.check('ptag'));
    this._resolveValueByGeo();
  }

  /**
   * Access to private properties from outside
   */
  get form(): UntypedFormGroup { return this._form; }

  get enhancedRegisterForm(): FormGroup {
    return this._enhancedRegisterForm;
  }
  /**
   * Returns FormControl by key
   *
   * @param key
   */
  input(key: string): AbstractControl {
    return this._form.get(key);
  }

  public onClickStep() {
    const controlsToCheck = ['email', 'password', 'mobile_phone', 'terms_acceptance'];

    let isFormValid = true;

    controlsToCheck.forEach(controlName => {
      const control = this.enhancedRegisterForm.get(controlName);
      if (control && control.invalid) {
        control.markAsTouched();
        isFormValid = false;
      }
    });
    if (!isFormValid) {
      return;
    }
    this.isSecondStep.set(true);
  }

  onEnhancedRegSubmit(missedFieldsForm: FormGroup) {

    missedFieldsForm.removeControl('receive_promos');
    missedFieldsForm.removeControl('receive_sms_promos');

    if (missedFieldsForm.invalid) {
      this._formErrors.applyFormErrors(missedFieldsForm, null, true);
      return;
    }

    this._userProfile.loading.set(true);

    const formData = {
      ...missedFieldsForm.getRawValue(),
      mobile_phone: this._userPhone.state().phonePrefix + this.enhancedRegisterForm.get('mobile_phone').value,
    };

    this._onSubmitReg$(this.enhancedRegisterForm, formData).pipe(
      switchMap(() => combineLatest([this._user.getUserInfo(), this._user.getPlayerStats()])),
      finalize(() => this._userProfile.loading.set(false)),
    ).subscribe();
  }

  onRegSubmit() {
    this._onSubmitReg$(this.form)
      .pipe(
        switchMap(() => combineLatest([this._user.getUserInfo(), this._user.getPlayerStats()])),
      )
      .subscribe();
  }

  /**
   * Submit form handler
   */
  private _onSubmitReg$(form: FormGroup, enhancedFormValues?: any): Observable<any> {
    const formRef = enhancedFormValues ? this.enhancedRegisterForm : this.form;

    this._setAdditionalFields(form);

    const errors = this._formErrors.applyFormErrors(form, null, true);

    if (!this._currency.isCryptoCurrency(form.value.currency) && !enhancedFormValues) {
      this.countryAndNicknameErrors = [
        ...errors.country,
        ...errors.nickname,
      ];
    }

    if (form.invalid) {
      return of(null);
    }

    this.loading.set(true);

    const bonusCode = form.getRawValue().bonus_code;

    const value = {
      ...form.getRawValue(),
      ...( !enhancedFormValues && {
        country: this._currency.cryptoCurrencyList().some((e) => e?.code?.includes(form.value.currency))
          ? this._env.dataEnv().country.short.toUpperCase()
          : form.value.country.toUpperCase()
      }),
      ...( enhancedFormValues?.mobile_phone && {
        mobile_phone: enhancedFormValues.mobile_phone,
      }),
      receive_promos: true,
      bonus_code: bonusCode ? bonusCode.toUpperCase() : bonusCode
    };

    return this._ssApi.usersSignUp({
      user: value
    }).pipe(
      switchMap((resp) => this._cacheCF.updateCache$().pipe(map(data => resp))),
      tap(response => {
        this._cache.clear();
        this._user.applyUserInfo(response);
        this._addUserToCryptoGroup(response);
        this._addUserToSamuraiGroupIfRtl();

        forkJoin([
          this._user.getUserAccounts(),
          this._user.getUserAccountsCompatibility()
        ]).subscribe();

        this._gtm.signUpSuccessTimestamp('reg_succ_time', 'reg_succ', response.id);
        this._gtm.signUpSuccess(response.id);

        this.registered$.next(true);
      }),
      switchMap(() => this.partners$),
      switchMap(() =>
        iif(
          () => !!enhancedFormValues,
          this.updateWithRetryWithoutPhone(enhancedFormValues),
          of(null)
        )
      ),
      tap(async (partners) => {
        await this._proceedRedirects(partners);
      }),
      switchMap(() => this._stag.resolveStagParam$()),
      tap(async () => {
        this._contentUpdater.updateAll();
      }),
      switchMap(() => !this._currency.isCryptoCurrency(form.value.currency) ? this._userInfo.updatePlayerInfo({
        nickname: form.value.nickname},
        'edition') : of(null)),
      switchMap(() => !this._currency.isCryptoCurrency(form.value.currency) ? this._user.getUserInfo() : of(null)),
      switchMap(() => this._rtl.isAr() ? EMPTY : this._userInfo.missingAttributesFor('deposit')),
      tap(() => this.toggleRegistrationDataToStorage(formRef,true)),
      catchError(error => {
        this._toastMessage.error('t.error-check-data');
        this._gtm.registerError('register_error', error.error.errors);
        this.error$.next(error);
        this._formErrors.applyFormErrors(form, {
          errors: {
            ...error.error.errors || {},
            ...error.error.errors && error.error.errors.profile || {},
          },
        }, true);
        return of(error);
      }),
      finalize(() => {
        this._ubidex.onRegister();
        this._zendesk.setInitFunctionalData();
        this.loading.set(false);
      })
    );
  }

  /**
   * Set values to fields that not exists in form
   *
   * @private
   */
  private _setAdditionalFields(form: UntypedFormGroup) {
    form.get('age_acceptance')?.setValue(form.get('terms_acceptance').value);
    form.get('password_confirmation')?.setValue(form.get('password').value);
  }

  /**
   * Automatically set known values for register form
   *
   * @private
   */
  private _setAutoDetectedValues() {
    if (!this._platform.isBrowser) {
      return;
    }

    if (this._env.dataEnv().currency.short && this._currency.currencyList().some(currency => currency.code === this._env.dataEnv().currency.short)) {
      this.form.get('currency').setValue(this._env.dataEnv().currency.short);
      this.enhancedRegisterForm.get('currency').setValue(this._env.dataEnv().currency.short);
    } else {
      this.form.get('currency').setValue('EUR');
      this.enhancedRegisterForm.get('currency').setValue('EUR');
    }

    if (this._env.dataEnv().country.short && this._country.countryList().some(country => country.short === this._env.dataEnv().country.short)) {
      this.form.get('country').setValue(this._env.dataEnv()?.country?.short);
    } else {
      this._showArrow = true;
    }
  }

  /**
   * Add user to crypto group if has crypto currency after register
   * @param response
   * @private
   */
  private _addUserToCryptoGroup(response: any) {
    if (this._currency.isCryptoCurrency(response.currency)) {
      this._groups.addToGroup(CRYPTO_GROUP).pipe(
      ).subscribe();
    }
  }

  /**
   * Add user to Samurai loyalty group if rtl.isAr()
   * @private
   */
  private _addUserToSamuraiGroupIfRtl() {
    if (this._rtl.isAr()) {
      this._groups.addToGroup(this._loyalty.Group.SAMURAI).subscribe();
    }
  }

  /**
   * Toggle cookies enter data for set if user from terms
   *
   * @param form
   * @param isRemove
   * @param step
   */
  public toggleRegistrationDataToStorage(form: FormGroup, isRemove: boolean = false, isFullReg?: boolean) {
    if (!isRemove) {
    const regData: any = {};
    const formRef = isFullReg ? this.enhancedRegisterForm : this.form;

    fieldsToSaveLocalStorage.forEach(field => regData[field] = formRef.get(field).value);

    this._localStorage.set(registrationStorageName, encodeToHex(encodeToBase64(JSON.stringify((regData)))));
    this._localStorage.set(isFullRegStorageName, JSON.stringify(Boolean(isFullReg)));
    } else {
      this._localStorage.clearItem(registrationStorageName);
      this._localStorage.clearItem(isFullRegStorageName);
      this._localStorage.clearItem(additionalInfoStorageName);
    }
  }

  /**
   * Paste data in modal registration from localStorage
   * @private
   */
  private _pasteDataFromStorageToForm() {
    if (this._localStorage.get(registrationStorageName)) {
      const data = JSON.parse(decodeFromBase64(decodeHex(this._localStorage.get(registrationStorageName))));
      const formRef = JSON.parse(this._localStorage.get(isFullRegStorageName)) ? this.enhancedRegisterForm : this.form;
      if (data) {
        Object.entries(data).forEach(value => {
          formRef.patchValue({[value[0]]: value[1]});
        });
      }
    }
  }

  private _getPartners$(): Observable<string[]> {
    return this._static.item({ slug: 'special-partners' }).pipe(
      filter(list => !!list),
      first(),
      map(list => list && list[0]),
      switchMap(({ Content }) => {
        const strippedString = Content.replace(/<[^>]*>/g, '').replace(/\n/g, '');
        return of(strippedString.split(','));
      }),
      catchError((err) => {
        return of(err)
      })
    );
  }


  private async _proceedRedirects(partners?: any) {
    if (Boolean(this._localStorage.get(STAG_URL_KEY)) && !!this._localStorage.get(STAG_URL_KEY)?.length &&
      partners?.includes(this._localStorage.get(STAG_KEY))) {
      await this._partnersRedirect();
      return;
    }

    if (Array.from(this._local.routes.keys()).some(key => key.includes('promo'))) {
      await this._promoRedirects();
    } else {
      await this._globalRedirects();
    }
  }

  /**
   * Redirect after registration
   * @private
   */
  private async _partnersRedirect() {
    await this._router.navigateByUrl(this._localStorage.get(STAG_URL_KEY));
    this._localStorage.clearItem(STAG_URL_KEY);
  }


  private async _promoRedirects() {
    if (this._local.getUrl('promo-hunt')) {
      await this._router.navigateByUrl(this._local.getUrl('promo-hunt').url);
    }
    if (this._local.getUrl('promo-deposit')) {
      await this._router.navigate(['account', 'deposit'], {
        queryParams: {
          bonusCode: this._local.getUrl('promo-deposit')?.data?.bonusCode,
          successUrl: this._local.getUrl('promo-deposit')?.data?.successUrl,
        },
      });
    }
    if (this._local.getUrl('promo-auth')) {
      await this._router.navigateByUrl(this._local.getUrl('promo-auth').url);
    }
    if (this._local.getUrl('promo-tournaments')) {
      await this._router.navigateByUrl(this._local.getUrl('promo-tournaments').url);
    }
  }

  private async _globalRedirects() {
    // If this._rtl.isAr() navigate to deposit and after this._userInfo.missingAttributesFor('deposit')
    await this._router.navigateByUrl(this._rtl.isAr() ? '/deposit' : '/loyalty-program').then(() => {
      this._toastMessage.success('t.register-success');
      this._localStorage.clearItem(registrationStorageName);
    });
  }

  private _resolveValueByGeo() {
    this._subscriptions.receiveEmailPromosValue$.pipe(
    ).subscribe((e) => {
      this.form.get('receive_promos').setValue(e);
      this.enhancedRegisterForm.get('receive_promos').setValue(e);
    });

    this._subscriptions.acceptedTermsValue$.pipe(
    ).subscribe((e) => {
      this.form.get('age_acceptance').setValue(e);
      this.form.get('terms_acceptance').setValue(e);

      this.enhancedRegisterForm.get('age_acceptance').setValue(e);
      this.enhancedRegisterForm.get('terms_acceptance').setValue(e);
    });
  }

  /**
   * Generate random nickname
   * @private
   */
  private _generateNickname(): string {
    let nickname = '';
    const symbols = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < 12; i++) {
      nickname += symbols.charAt(Math.floor(Math.random() * symbols.length));
    }

    return nickname;
  }

  private updateWithRetryWithoutPhone(values: Record<string, any>) {
    const { mobile_phone, ...fallback } = values ?? {};
    return this._userInfo.updatePlayerInfo(values, 'edition').pipe(
      catchError(() => this._userInfo.updatePlayerInfo(fallback, 'edition')),
      catchError(() => of(null))
    );
  }
}
